// The family's account manager — their point of contact, and their vendors', until the function.
import React from 'react';
import { useQuery } from '@apollo/client';
import { Paper, Stack, Avatar, Box, Typography, Link, Chip } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { MY_ACCOUNT_MANAGER_QUERY } from '../graphql/queries';

const fmtDate = (d) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

// Contact details only (used inline, e.g. on a vendor's booking)
export function AccountManagerContact({ manager, label = 'Point of contact' }) {
  if (!manager) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <SupportAgentIcon fontSize="small" color="primary" />
      <Typography variant="body2"><strong>{label}:</strong> {manager.name}</Typography>
      {manager.phone && <Link variant="body2" href={`tel:${manager.phone}`}>{manager.phone}</Link>}
      <Link variant="body2" href={`mailto:${manager.email}`}>{manager.email}</Link>
    </Stack>
  );
}

// The card for the family (loads the family's account manager itself)
export default function AccountManagerCard({ sx }) {
  const { data } = useQuery(MY_ACCOUNT_MANAGER_QUERY, { fetchPolicy: 'cache-and-network' });
  const m = data?.myAccountManager;
  if (!m || !m.active) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: 'primary.light', bgcolor: '#fcf7fd', ...sx }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><SupportAgentIcon /></Avatar>
        <Box flex={1} minWidth={0}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" fontWeight={700}>{m.name}</Typography>
            <Chip size="small" color="primary" variant="outlined" label="Your account manager" />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Your point of contact — and your vendors' — {m.endsOn ? `until your function on ${fmtDate(m.endsOn)}` : 'throughout your wedding planning'}.
          </Typography>
        </Box>
        <Stack spacing={0.5} flexShrink={0}>
          {m.phone && (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <PhoneIcon fontSize="small" color="action" />
              <Link href={`tel:${m.phone}`} variant="body2">{m.phone}</Link>
            </Stack>
          )}
          <Stack direction="row" spacing={0.75} alignItems="center">
            <EmailIcon fontSize="small" color="action" />
            <Link href={`mailto:${m.email}`} variant="body2">{m.email}</Link>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}
